import React from 'react';
import { useCurrentFrame, interpolate, AbsoluteFill } from 'remotion';
import { COLORS } from '../constants';

interface GlowOrbProps {
  x: number;
  y: number;
  size: number;
  color: string;
  delay?: number;
  speed?: number;
}

const GlowOrb: React.FC<GlowOrbProps> = ({ x, y, size, color, delay = 0, speed = 0.5 }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;

  const floatY = Math.sin(f * speed * 0.02) * 30;
  const floatX = Math.cos(f * speed * 0.015) * 20;
  const scaleBreath = 1 + Math.sin(f * speed * 0.01) * 0.1;
  const opacity = interpolate(f, [0, 30], [0, 0.25], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color}20, ${color}08, transparent)`,
        filter: `blur(${size * 0.3}px)`,
        transform: `translate(${floatX}px, ${floatY}px) scale(${scaleBreath})`,
        opacity,
      }}
    />
  );
};

interface GlowBackgroundProps {
  variant?: 'default' | 'warm' | 'cool' | 'dramatic';
}

export const GlowBackground: React.FC<GlowBackgroundProps> = ({ variant = 'default' }) => {
  const configs: Record<string, GlowOrbProps[]> = {
    default: [
      { x: 20, y: 30, size: 500, color: '#3B82F6', speed: 0.5 },
      { x: 70, y: 50, size: 400, color: '#8B5CF6', speed: 0.7, delay: 10 },
      { x: 50, y: 70, size: 350, color: '#06B6D4', speed: 0.4, delay: 20 },
    ],
    warm: [
      { x: 25, y: 40, size: 500, color: '#F59E0B', speed: 0.5 },
      { x: 65, y: 30, size: 450, color: '#EF4444', speed: 0.6, delay: 15 },
      { x: 45, y: 65, size: 350, color: '#8B5CF6', speed: 0.4, delay: 25 },
    ],
    cool: [
      { x: 30, y: 35, size: 500, color: '#06B6D4', speed: 0.5 },
      { x: 60, y: 55, size: 450, color: '#3B82F6', speed: 0.6, delay: 10 },
      { x: 40, y: 75, size: 300, color: '#10B981', speed: 0.4, delay: 20 },
    ],
    dramatic: [
      { x: 50, y: 40, size: 600, color: '#3B82F6', speed: 0.3 },
      { x: 30, y: 60, size: 500, color: '#8B5CF6', speed: 0.5, delay: 5 },
      { x: 70, y: 30, size: 400, color: '#06B6D4', speed: 0.7, delay: 15 },
      { x: 50, y: 80, size: 300, color: '#10B981', speed: 0.4, delay: 25 },
    ],
  };

  const orbs = configs[variant] || configs.default;

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        overflow: 'hidden',
      }}
    >
      {/* Subtle dot grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(#CBD5E140 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          opacity: 0.4,
        }}
      />
      {/* Orbs */}
      {orbs.map((orb, i) => (
        <GlowOrb key={i} {...orb} />
      ))}
    </AbsoluteFill>
  );
};

interface SceneTransitionProps {
  direction?: 'in' | 'out';
  durationFrames?: number;
}

export const SceneTransition: React.FC<SceneTransitionProps> = ({
  direction = 'in',
  durationFrames = 15,
}) => {
  const frame = useCurrentFrame();

  const opacity =
    direction === 'in'
      ? interpolate(frame, [0, durationFrames], [1, 0], {
          extrapolateRight: 'clamp',
        })
      : interpolate(frame, [0, durationFrames], [0, 1], {
          extrapolateRight: 'clamp',
        });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: COLORS.bg,
        opacity,
        zIndex: 100,
      }}
    />
  );
};
